import React from 'react';
import { Typography, Button, Progress, Divider } from 'antd';
import { useDarkMode } from 'src/context/DarkModeContext';
import { QuizQuestionAnswer } from 'src/data/models/quiz/QuizQuestionResultModel';
import Confetti from 'react-confetti';
import QuizMistakesOverview from './QuizMistakesOverview';

const { Title, Text } = Typography;

interface QuizResultScreenProps {
  startNewQuiz: () => void;
  quizAnswers: QuizQuestionAnswer[];
}

const QuizResultScreen: React.FC<QuizResultScreenProps> = ({
  quizAnswers,
  startNewQuiz,
}) => {
  const { isDarkMode } = useDarkMode();

  const totalQuestionNumber = quizAnswers.length;
  const incorrectAnswers = quizAnswers.filter((answer) => !answer.isCorrect);
  const correctAnswersCount = quizAnswers.filter(answer => answer.isCorrect).length;

  const textColor = isDarkMode ? '#ffffff' : '#000000';

  const generateResultStatusMessage = (): string => {
    return correctAnswersCount === totalQuestionNumber
      ? 'Perfect! 🎉 You nailed it!'
      : correctAnswersCount >= totalQuestionNumber * 0.7
        ? 'Awesome job! 💪'
        : correctAnswersCount >= totalQuestionNumber * 0.4
          ? 'Nice try, keep going! 👍'
          : 'Don’t worry, you’ll get it next time! 😊';
  }

  return (
    <div
      style={{
        color: textColor,
        minHeight: '100vh',
        padding: '40px 20px',
        position: 'relative',
      }}
    >
      <Confetti width={window.innerWidth} height={window.innerHeight} recycle={false} />

      <div
        style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
        <Title level={2} style={{ color: isDarkMode ? '#ffffff' : '#003a8c' }}>
          Quiz Completed!
        </Title>

        <Progress
          type="circle"
          percent={Math.round((correctAnswersCount / totalQuestionNumber) * 100)}
          format={() => `${correctAnswersCount} / ${totalQuestionNumber}`}
          strokeColor={isDarkMode ? '#40a9ff' : '#1890ff'}
          className={isDarkMode ? 'dark' : 'light'}
          style={{ color: isDarkMode ? '#ffffff' : '#003a8c' }}
        />

        <Text style={{
          display: 'block',
          marginTop: 20,
          fontSize: 16,
          color: isDarkMode ? '#ffffff' : '#003a8c'
        }}>
          {generateResultStatusMessage()}
        </Text>

        <Divider />

        <QuizMistakesOverview incorrectAnswers={incorrectAnswers} />

        <Divider />

        <Button
          type="primary"
          size="large"
          onClick={startNewQuiz}
          style={{
            backgroundColor: isDarkMode ? '#3949AB' : '#003a8c',
            border: 'none',
          }}
        >
          Try Another Quiz
        </Button>
      </div>
    </div>
  );
};

export default QuizResultScreen;
